import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { useHistory, useLocation } from 'react-router';
import { Helmet } from 'react-helmet';
import Box from '@mui/material/Box';
import { Alert } from '@mui/material';
import {
  logPostHogEvent,
  getParamsFromURLSearch,
  getCurrentDealEventStrObj
} from '@utils/index';
import PageContainer from '@components/PageContainer';

import MostPopular from '@components/MostPopular';
import trackUse from '@utils/trackUse';
import {
  useGetVariationsForASIN,
  useResolveShortURL
} from '@hooks/useVariations';
import { getASINFromURL } from '@utils/URL';
import { getSEOSchema, getHelmetMeta } from '@utils/seo';
import Loading from '@components/Loading';
import { VariationType } from '@types';
import Blog from '@pages/Blog/Blog';
import config from '@configFile';
import Instructions from './components/Instructions';
import APINotice from './components/APINotice';
import SearchField from './components/SearchField';
import VariationsContent from './components/VariationsContent';
import VariationsFAQ from './components/VariationsFAQ';

const Variations = () => {
  const event = getCurrentDealEventStrObj();
  const history = useHistory();
  const [variations, setVariations] = useState<VariationType[]>([]);
  const [error, setError] = useState('');
  const [ASIN, setASIN] = useState<string | null>(null);
  const [URL, setURL] = useState('');
  const [debouncedURL, setDebouncedURL] = useState('');
  const { mutate: resolveShort, isLoading: isResolvingShortUrl } =
    useResolveShortURL();
  const { mutate: getVariations, isLoading: isFetchingVariations } =
    useGetVariationsForASIN();
  const [isDirectLink, setIsDirectLink] = useState(false);
  const location = useLocation();
  const { search } = location;
  const searchASIN =
    getParamsFromURLSearch(search)?.ASIN ||
    getParamsFromURLSearch(search)?.asin ||
    null;
  const locationASIN = useMemo(() => {
    if (searchASIN) {
      return searchASIN;
    }
    return null;
  }, [searchASIN]);

  const pageTitle = 'Compare Amazon Product Price Variations';
  const isLoading = isResolvingShortUrl || isFetchingVariations;

  const getAllVariations = (
    ASIN: string,
    isDirect: boolean,
    URL: string,
    isLoading: boolean
  ) => {
    if (isLoading) {
      return;
    }
    getVariations(
      { ASIN, isDirect, URL },
      {
        onSuccess: (data) => {
          if (data?.length === 0) {
            setError(
              `Amazon's API is not returning any variations for this product. This could be due to the product being out of stock or not available.`
            );
          } else {
            setError('');
          }
          setVariations(data);
        },
        onError: () => {
          setError(
            `This item is not accessible through Amazon’s API, try another product link.`
          );
          setVariations([]);
        }
      }
    );
  };

  useEffect(() => {
    if (locationASIN && locationASIN !== ASIN && locationASIN.length === 10) {
      setASIN(locationASIN);
      getAllVariations(locationASIN, true, URL, isLoading);
      setURL(`https://www.amazon.com/gp/product/${locationASIN}`);
      setDebouncedURL(`https://www.amazon.com/gp/product/${locationASIN}`);
    }
  }, [locationASIN]);

  useEffect(() => {
    if (ASIN) {
      setVariations([]);
      getAllVariations(ASIN, isDirectLink, URL, isLoading);
    }
  }, [ASIN]);

  const handleChangingUrl = (url: string) => {
    setDebouncedURL(url);
    debouncedUrlChange(url);
    setError('');
  };

  const debouncedUrlChange = React.useRef(
    debounce((url: string) => {
      handleChangeURL(url);
    }, 500)
  ).current;

  const handleChangeURL = (url: string) => {
    setIsDirectLink(false);
    const urlASIN = getASINFromURL(url) === '' ? null : getASINFromURL(url);

    if (urlASIN && urlASIN !== ASIN) {
      // user has entered a new valid link
      trackUse({
        type: 'TEXT',
        value: url,
        item: 'variations-URL-text'
      });

      logPostHogEvent('variations-input-URL-text', {
        value: url,
        type: 'TEXT'
      });

      setURL(`https://www.amazon.com/gp/product/${urlASIN}`);
      setDebouncedURL(`https://www.amazon.com/gp/product/${urlASIN}`);
      setASIN(urlASIN);
      history.push(`/variations?ASIN=${urlASIN}`);
    } else {
      if (
        url.toLowerCase().includes('https://a.co/d') ||
        url.toLowerCase().includes('https://amzn.to')
      ) {
        resolveShort(
          {
            URL: url
          },
          {
            onSuccess: (data) => {
              if (data?.fullURL) {
                handleChangeURL(data.fullURL);
              }
            }
          }
        );
      }

      // user has entered a new invalid link
      setURL(url);
      setASIN(null);
      history.push(`/variations`);
      setVariations([]);
    }
  };

  const renderErrorMessage = () => {
    if (error) {
      return (
        <Box textAlign="center">
          <Alert variant="outlined" severity="warning">
            {error}
          </Alert>
        </Box>
      );
    }

    return null;
  };

  const image = 'https://www.jungledealsblog.com/jd-images/page-deals/3.jpg';

  const description =
    'Discover the best Amazon deals with our comprehensive list of name-brand products featuring exclusive coupons and special offers. Save big on your favorite items with our daily updated discounts.';
  return (
    <>
      <PageContainer fullWidth title={pageTitle}>
        <Helmet
          title={pageTitle}
          meta={getHelmetMeta({
            title: pageTitle,
            image,
            description,
            url: '/variations'
          })}
          script={[
            {
              type: 'application/ld+json',
              innerHTML: JSON.stringify(
                getSEOSchema({
                  title: pageTitle,
                  image: [image],
                  description
                })
              )
            }
          ]}
          link={[
            {
              rel: 'canonical',
              href: `${config.CURRENT_URL}/variations`
            }
          ]}
        />
        <Instructions showMain={!ASIN} />
        {event?.shouldShowAPINotice ? <APINotice /> : null}
        <SearchField value={debouncedURL} onChange={handleChangingUrl} />
        {renderErrorMessage()}
        {isLoading ? (
          <Loading margin="16px auto" minHeight="initial" showMessage />
        ) : null}
        {variations?.length > 0 ? (
          <VariationsContent ASIN={ASIN} variations={variations} />
        ) : null}
        <MostPopular />
        <VariationsFAQ />
      </PageContainer>
      <Box marginTop="16px">
        <Blog shouldRenderSEO={false} />
      </Box>
    </>
  );
};

export default Variations;
